/**
 * Unified AI Handler for DapperGPT
 * 
 * This module provides a unified interface for all AI providers using AI SDK 6.
 * It maintains backwards compatibility with the legacy API while enabling
 * new features like tools, agents, and MCP.
 * 
 * Architecture:
 * - Uses AI SDK 6 internally for all providers
 * - Maintains legacy type compatibility (GPTChatMessage, GPTChatResult)
 * - Supports tools via AI SDK 6's tool calling
 * - Ready for agents and MCP integration
 */

import {
    getModelProvider,
    getModelConfig,
    type AIProvider,
    type ChatModel
} from "../constant";
import {
    streamChatWithAISDK,
    generateChatWithAISDK,
    type AISDKStreamOptions,
} from "./ai-sdk";
import { availableTools, type ToolName } from "./ai-tools";
import type {
    GPTChatMessage,
    GPTChatResult,
    OnMessageCallback,
    OpenAiChatRequestHandlerExtra
} from "./openai";

// =============================================================================
// API KEY CONFIGURATION
// =============================================================================

export interface AIApiKeys {
    openai?: string;
    anthropic?: string;
    google?: string;
    xai?: string;
    deepseek?: string;
    glm?: string;
    kimi?: string;
    qwen?: string;
}

// Legacy type for backwards compatibility
export type LegacyApiKeys = {
    openAi: string;
    anthropic: string;
};

/**
 * Convert a TeamCredential.credentials JSON blob into AIApiKeys.
 *
 * The `team_credential` table stores every provider's key in a single JSON
 * column, shaped as documented on the Prisma model:
 *   { "openai": "sk-...", "anthropic": "sk-ant-...", "google": "...",
 *     "xai": "...", "deepseek": "...", "glm": "...", "kimi": "...", "qwen": "..." }
 *
 * Use this everywhere rather than reading the blob inline — several API routes
 * previously hardcoded `{ openAi: key, anthropic: '' }`, which silently made
 * every non-OpenAI model unusable with team credentials even when the key was
 * stored. `openAi` is also accepted as a key alias for older rows.
 */
export const credentialsToApiKeys = (credentials: unknown): AIApiKeys => {
    const raw = (credentials ?? {}) as Record<string, unknown>;
    const read = (key: string): string | undefined => {
        const value = raw[key];
        return typeof value === 'string' && value.length > 0 ? value : undefined;
    };
    return {
        openai: read('openai') ?? read('openAi'),
        anthropic: read('anthropic'),
        google: read('google'),
        xai: read('xai'),
        deepseek: read('deepseek'),
        glm: read('glm') ?? read('zhipu'),
        kimi: read('kimi') ?? read('moonshot'),
        qwen: read('qwen') ?? read('alibaba') ?? read('dashscope'),
    };
};

/**
 * Pick the single API key string appropriate for `model` out of a credentials
 * blob, falling back to the OpenAI key. For endpoints that still take one key
 * rather than the full AIApiKeys map.
 */
export const pickProviderKey = (credentials: unknown, model?: ChatModel): string => {
    const keys = credentialsToApiKeys(credentials);
    const provider = model ? getModelProvider(model) : undefined;
    return (provider ? keys[provider] : undefined) ?? keys.openai ?? '';
};

/** True when at least one provider key is present. */
export const hasAnyApiKey = (keys: AIApiKeys): boolean =>
    Object.values(keys).some(v => typeof v === 'string' && v.length > 0);

// =============================================================================
// TOOL CONFIGURATION
// =============================================================================

export interface AIToolConfig {
    enabled: boolean;
    tools?: ToolName[];
}

// =============================================================================
// ENHANCED REQUEST OPTIONS
// =============================================================================

export interface AIRequestOptions extends OpenAiChatRequestHandlerExtra {
    // Tool calling
    tools?: ToolName[];
    toolChoice?: 'auto' | 'none' | 'required';

    // Tool callbacks
    onToolCall?: (toolName: string, args: unknown) => void;
    onToolResult?: (toolName: string, result: unknown) => void;

    // Agent mode (future)
    agentMode?: boolean;
    maxSteps?: number;

    // MCP (future)
    mcpServers?: string[];
}

// =============================================================================
// UNIFIED AI HANDLER (AI SDK 6)
// =============================================================================

/**
 * Unified AI chat request handler using AI SDK 6
 * 
 * This is the new primary handler that routes all requests through AI SDK 6.
 * It supports all providers: OpenAI, Anthropic, Google, xAI, DeepSeek, GLM, Kimi and Qwen.
 * 
 * @example
 * // Basic usage (backwards compatible)
 * const result = await aiChatRequestHandler(apiKeys, 'gpt-4o', messages);
 * 
 * @example
 * // With streaming
 * await aiChatRequestHandler(apiKeys, 'claude-sonnet-4-5', messages, signal, {
 *   useStreaming: true,
 *   onMessage: (text, first, last) => console.log(text),
 * });
 * 
 * @example
 * // With tools (new!)
 * await aiChatRequestHandler(apiKeys, 'gpt-4o', messages, signal, {
 *   tools: ['calculator', 'dateTime', 'webScraper'],
 *   useStreaming: true,
 *   onMessage: callback,
 * });
 */
export const aiChatRequestHandler = async <T extends AIRequestOptions | undefined>(
    apiKey: AIApiKeys | LegacyApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    // Normalize API keys (support both old and new format)
    const normalizedKeys: AIApiKeys = 'openAi' in apiKey
        ? { openai: apiKey.openAi, anthropic: apiKey.anthropic }
        : apiKey;

    const provider = getModelProvider(model);
    const config = getModelConfig(model);

    // Validate API key exists for provider
    const providerKey = getApiKeyForProvider(normalizedKeys, provider);
    if (!providerKey) {
        throw new Error(
            `No API key configured for ${config?.displayName || model}. ` +
            `Please add your ${getProviderDisplayName(provider || 'openai')} API key.`
        );
    }

    try {
        if (extra?.useStreaming && extra?.onMessage) {
            // Streaming mode using AI SDK 6
            // Build tools if specified
            let tools: Record<string, (typeof availableTools)[ToolName]> | undefined;
            if (extra.tools && extra.tools.length > 0) {
                tools = {};
                for (const toolName of extra.tools) {
                    if (availableTools[toolName]) {
                        tools[toolName] = availableTools[toolName];
                    }
                }
            }

            await streamChatWithAISDK(model, messages, normalizedKeys, {
                onMessage: extra.onMessage,
                temperature: extra.temperature,
                maxTokens: extra.maxTokens,
                system: extra.system,
                signal: signal?.signal,
                tools,
                toolChoice: extra.toolChoice,
                maxSteps: extra.maxSteps,
                onToolCall: extra.onToolCall,
                onToolResult: extra.onToolResult,
            });
            return null as T extends { useStreaming: true } ? null : GPTChatResult;
        } else {
            // Non-streaming mode using AI SDK 6
            // Build tools if specified
            let tools: Record<string, (typeof availableTools)[ToolName]> | undefined;
            if (extra?.tools && extra.tools.length > 0) {
                tools = {};
                for (const toolName of extra.tools) {
                    if (availableTools[toolName]) {
                        tools[toolName] = availableTools[toolName];
                    }
                }
            }

            const result = await generateChatWithAISDK(model, messages, normalizedKeys, {
                temperature: extra?.temperature,
                maxTokens: extra?.maxTokens,
                system: extra?.system,
                signal: signal?.signal,
                tools,
                toolChoice: extra?.toolChoice,
                maxSteps: extra?.maxSteps,
            });

            // Convert to legacy GPTChatResult format
            return convertToGPTChatResult(result, model) as T extends { useStreaming: true } ? null : GPTChatResult;
        }
    } catch (error: unknown) {
        const err = error as Error;
        throw new Error(formatAIError(err, config?.displayName || model));
    }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the appropriate API key for a provider
 */
const getApiKeyForProvider = (keys: AIApiKeys, provider?: AIProvider): string | undefined => {
    if (!provider) return keys.openai;

    switch (provider) {
        case 'openai':
            return keys.openai;
        case 'anthropic':
            return keys.anthropic;
        case 'google':
            return keys.google;
        case 'xai':
            return keys.xai;
        case 'deepseek':
            return keys.deepseek;
        case 'glm':
            return keys.glm;
        case 'kimi':
            return keys.kimi;
        case 'qwen':
            return keys.qwen;
        default:
            return keys.openai;
    }
};

/**
 * Convert AI SDK result to legacy GPTChatResult format
 */
const convertToGPTChatResult = (
    result: { text: string; usage?: { promptTokens: number; completionTokens: number } },
    model: string
): GPTChatResult => {
    return {
        id: `ai-sdk-${Date.now()}`,
        object: 'chat.completion',
        created: Date.now(),
        model,
        choices: [{
            index: 0,
            message: { role: 'assistant', content: result.text },
            finish_reason: 'stop',
        }],
        usage: {
            prompt_tokens: result.usage?.promptTokens || 0,
            completion_tokens: result.usage?.completionTokens || 0,
            total_tokens: (result.usage?.promptTokens || 0) + (result.usage?.completionTokens || 0),
        },
    };
};

/**
 * Format AI errors for user-friendly display
 */
const formatAIError = (error: Error, modelName: string): string => {
    const message = error?.message || 'Unknown error occurred';

    if (message.includes('rate limit') || message.includes('429')) {
        return 'Rate limit exceeded. Please wait a moment and try again.';
    }

    if (message.includes('context') || message.includes('tokens')) {
        return `Message too long for ${modelName}. Try using a shorter message.`;
    }

    if (message.includes('api key') || message.includes('authentication') || message.includes('401')) {
        return 'Invalid API key. Please check your API key and try again.';
    }

    if (message.includes('does not exist') || message.includes('not found')) {
        return `${modelName} is not available. Make sure you have access to this model.`;
    }

    return message;
};

/**
 * Validate that required API keys are configured for a set of models
 */
export const validateApiKeysForModels = (
    keys: AIApiKeys,
    models: ChatModel[]
): { valid: boolean; missingProviders: AIProvider[] } => {
    const providers = new Set(models.map(m => getModelProvider(m)).filter(Boolean)) as Set<AIProvider>;
    const missingProviders: AIProvider[] = [];

    providers.forEach(provider => {
        const key = getApiKeyForProvider(keys, provider);
        if (!key) {
            missingProviders.push(provider);
        }
    });

    return {
        valid: missingProviders.length === 0,
        missingProviders,
    };
};

/**
 * Get a friendly display name for an API provider
 */
export const getProviderDisplayName = (provider: AIProvider): string => {
    const names: Record<AIProvider, string> = {
        openai: 'OpenAI',
        anthropic: 'Anthropic',
        google: 'Google AI',
        xai: 'xAI (Grok)',
        deepseek: 'DeepSeek',
        glm: 'Z.AI (GLM)',
        kimi: 'Moonshot (Kimi)',
        qwen: 'Alibaba (Qwen)',
    };
    return names[provider] || provider;
};

// =============================================================================
// ADVANCED FEATURES (Tools, Agents, MCP)
// =============================================================================

/**
 * AI Chat with Tool Calling Support
 * 
 * This handler enables AI models to use tools during conversation.
 * Tools are executed automatically and results are fed back to the model.
 * 
 * @example
 * const result = await aiChatWithTools(apiKeys, 'gpt-4o', messages, {
 *   tools: ['calculator', 'dateTime'],
 *   onMessage: (text) => console.log(text),
 * });
 */
export const aiChatWithTools = async (
    apiKeys: AIApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    options: {
        tools: ToolName[];
        onMessage?: OnMessageCallback;
        onToolCall?: (toolName: string, args: unknown) => void;
        onToolResult?: (toolName: string, result: unknown) => void;
        maxToolRounds?: number;
        signal?: AbortSignal;
    }
): Promise<{ text: string; toolCalls: { name: string; args: unknown; result: unknown }[] }> => {
    // Build tool set from names
    const toolSet: Record<string, (typeof availableTools)[ToolName]> = {};
    for (const name of options.tools) {
        if (availableTools[name]) {
            toolSet[name] = availableTools[name];
        }
    }

    const result = await streamChatWithAISDK(model, messages, apiKeys, {
        onMessage: options.onMessage,
        signal: options.signal,
        tools: Object.keys(toolSet).length > 0 ? toolSet : undefined,
        toolChoice: 'auto',
        maxSteps: options.maxToolRounds || 5,
        onToolCall: options.onToolCall,
        onToolResult: options.onToolResult,
    });

    return {
        text: result.text,
        toolCalls: result.toolCalls?.map(tc => ({
            name: tc.toolName,
            args: tc.args,
            result: tc.result,
        })) || [],
    };
};

/**
 * Agent Mode - Multi-step autonomous AI execution
 * 
 * Uses AI SDK 6's ToolLoopAgent for autonomous multi-step tool calling.
 * The agent receives a task, decides which tools to use, and loops
 * until the task is complete or maxSteps is reached.
 * 
 * @example
 * const result = await aiAgentExecute(apiKeys, 'gpt-4o', 'Research AI trends', {
 *   tools: ['webSearch', 'webScraper'],
 *   maxSteps: 10,
 *   onStep: (step, action) => console.log(`Step ${step}: ${action}`),
 * });
 */
export const aiAgentExecute = async (
    apiKeys: AIApiKeys,
    model: ChatModel,
    task: string,
    options?: {
        tools?: ToolName[];
        maxSteps?: number;
        onStep?: (step: number, action: string) => void;
    }
): Promise<{ result: string; steps: { action: string; result: string }[] }> => {
    const { runAgent, AGENT_CONFIGS } = await import('./ai-agent');

    // Build tool set
    const toolSet: Record<string, (typeof availableTools)[ToolName]> = {};
    if (options?.tools) {
        for (const name of options.tools) {
            if (availableTools[name]) {
                toolSet[name] = availableTools[name];
            }
        }
    }

    const agentResult = await runAgent({
        config: {
            ...AGENT_CONFIGS.assistant(),
            tools: Object.keys(toolSet).length > 0 ? toolSet : undefined,
            maxSteps: options?.maxSteps || 10,
        },
        modelId: model,
        apiKeys,
        prompt: task,
    });

    // Call onStep for each step
    agentResult.steps.forEach((step, i) => {
        const action = step.toolCalls?.map(tc => tc.toolName).join(', ') || 'thinking';
        options?.onStep?.(i + 1, action);
    });

    return {
        result: agentResult.text,
        steps: agentResult.steps.map(step => ({
            action: step.toolCalls?.map(tc => `${tc.toolName}(${JSON.stringify(tc.input)})`).join(', ') || 'generate text',
            result: step.text || step.toolCalls?.map(tc => JSON.stringify(tc.output)).join(', ') || '',
        })),
    };
};

/**
 * MCP (Model Context Protocol) Integration
 * 
 * Connects to MCP servers, discovers their tools, and uses them in a chat.
 * MCP tools are merged with any built-in tools and passed to the AI SDK.
 * 
 * @example
 * const result = await aiWithMCP(apiKeys, 'gpt-4o', messages, {
 *   servers: [{ id: 'github', name: 'GitHub', transport: { type: 'http', url: 'https://mcp.example.com' } }],
 * });
 */
export const aiWithMCP = async (
    apiKeys: AIApiKeys,
    model: ChatModel,
    messages: GPTChatMessage[],
    mcpConfig: {
        servers: import('./ai-mcp').MCPServerConfig[];
        extraTools?: Record<string, unknown>;
        onMessage?: OnMessageCallback;
        signal?: AbortSignal;
        maxSteps?: number;
    }
): Promise<{ text: string; toolCalls: { name: string; args: unknown; result: unknown }[] }> => {
    const { getMCPManager } = await import('./ai-mcp');
    const manager = getMCPManager();

    // Connect to all servers
    for (const server of mcpConfig.servers) {
        if (!manager.isConnected(server.id)) {
            await manager.connect(server);
        }
    }

    // Get all MCP tools
    const mcpTools = manager.getAllTools();

    // Merge with any extra built-in tools
    const allTools = {
        ...mcpTools,
        ...(mcpConfig.extraTools || {}),
    };

    const result = await streamChatWithAISDK(model, messages, apiKeys, {
        onMessage: mcpConfig.onMessage,
        signal: mcpConfig.signal,
        tools: allTools as Record<string, (typeof availableTools)[ToolName]>,
        toolChoice: 'auto',
        maxSteps: mcpConfig.maxSteps || 10,
    });

    return {
        text: result.text,
        toolCalls: result.toolCalls?.map(tc => ({
            name: tc.toolName,
            args: tc.args,
            result: tc.result,
        })) || [],
    };
};

// =============================================================================
// LEGACY PROVIDER HANDLERS (Deprecated - use aiChatRequestHandler)
// =============================================================================

/**
 * @deprecated Use aiChatRequestHandler instead. This is kept for backwards compatibility.
 */
export const googleChatRequestHandler = async <T extends OpenAiChatRequestHandlerExtra | undefined>(
    _apiKey: string,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    console.warn('googleChatRequestHandler is deprecated. Use aiChatRequestHandler instead.');
    return aiChatRequestHandler({ google: _apiKey }, model, messages, signal, extra);
};

/**
 * @deprecated Use aiChatRequestHandler instead. This is kept for backwards compatibility.
 */
export const xaiChatRequestHandler = async <T extends OpenAiChatRequestHandlerExtra | undefined>(
    _apiKey: string,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    console.warn('xaiChatRequestHandler is deprecated. Use aiChatRequestHandler instead.');
    return aiChatRequestHandler({ xai: _apiKey }, model, messages, signal, extra);
};

/**
 * @deprecated Use aiChatRequestHandler instead. This is kept for backwards compatibility.
 */
export const deepseekChatRequestHandler = async <T extends OpenAiChatRequestHandlerExtra | undefined>(
    _apiKey: string,
    model: ChatModel,
    messages: GPTChatMessage[],
    signal?: AbortController,
    extra?: T
): Promise<T extends { useStreaming: true } ? null : GPTChatResult> => {
    console.warn('deepseekChatRequestHandler is deprecated. Use aiChatRequestHandler instead.');
    return aiChatRequestHandler({ deepseek: _apiKey }, model, messages, signal, extra);
};
